import * as React from "react";
import {
  useEffect,
  useState,
  FunctionComponent,
} from "react";
import {
  styled,
  Block,
} from "../../../seenspire-library";
import { sample, random } from "lodash";
import { OverlayDirection } from "./interfaces";
import { useImage, useImages } from "../../util";
import { Spinner } from "../../components/Spinner";

const AsyncImageStyled = styled.div.attrs({})<{ loaded: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1;
  opacity: ${({ loaded }) => (loaded ? 1 : 0)};
  transition: all 500ms ease;
`;
const AspectRatioBoxImageHolder = styled(Block).attrs({})<{
  url: string;
  bgTransition?: boolean;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  transition: ${({ bgTransition }) => bgTransition && "all 1s ease-in-out"};
  background-image: url(${({ url }) => url});
`;
const AspectRatioBoxImage = styled.img`
  display: none;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1;
`;

const OverlayLeft = `linear-gradient(to bottom,rgba(0,0,0,0) 20%,rgba(0, 0, 0, 0.4) 70%)`;
const SliderItemOverlay = styled(Block)<{
  isActive?: boolean;
  overlayDirection?: OverlayDirection;
}>`
  /* Image source is added here */
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 2;
  background: ${({ overlayDirection, isActive }) => {
    switch (overlayDirection) {
      case "left":
        return OverlayLeft;
      default:
        return `linear-gradient(to bottom,rgba(0,0,0,0) 20%,rgba(0, 0, 0, 0.4) 70%)`;
    }
  }};
`;

const LoadingOverlay = styled(Block)<{
  isActive?: boolean;
  overlayDirection?: OverlayDirection;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  > div {
    width: 24px;
    height: 24px;
    > div {
      background-color: ${({ theme }) => theme.colors.primary.base};
    }
  }
`;

const sampleImage = (size: "thumb" | "fullHd" | "original") =>
  `https://storage.googleapis.com/frontendtesting/frontend/seenspire-smaple-pictures/${size}/image${random(
    17
  )}.jpg`;

const AsyncImage: FunctionComponent<{
  src: any;
  isActive?: boolean;
  skipOverlay?: boolean;
  overlayDirection?: OverlayDirection;
  media?: any;
  rotating?: boolean;
  onImageLoaded?: () => void;
}> = ({
  src,
  isActive,
  skipOverlay,
  overlayDirection,
  media,
  rotating,
  onImageLoaded,
}) => {
  const [fallbackURL] = useState(sampleImage("thumb"));

  const [url, setUrl] = useState("");
  const [image, loaded] = useImage({ url: src || fallbackURL });
  const [images, imagesLoaded] = useImages(media);

  useEffect(() => {
    if (!imagesLoaded && !rotating) return;

    const item = sample(images);
    item && setUrl(item.url);

    const rotator = setInterval(() => {
      const item = sample(images);
      item && setUrl(item.url);
    }, 1500);
    return () => {
      clearInterval(rotator);
    };
  }, [imagesLoaded]);

  useEffect(() => {
    setUrl(src || fallbackURL);
    onImageLoaded && onImageLoaded();
  }, [loaded]);

  return (
    <>
      {!loaded && (<LoadingOverlay>
        <Spinner />
        </LoadingOverlay>
      )}
      <AsyncImageStyled loaded={loaded}>
        {!skipOverlay && (
          <SliderItemOverlay
            overlayDirection={overlayDirection}
            isActive={isActive}
          />
        )}
        <AspectRatioBoxImageHolder url={url} bgTransition={imagesLoaded} />
      </AsyncImageStyled>
    </>
  );
};
export default AsyncImage;
